<template>
  <el-dialog
    title="订单沟通记录"
    :visible="visible"
    width="90%"
    top="3vh"
    custom-class="matching-record-dialog"
    append-to-body
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="record-dialog">
      <div class="order-summary" v-loading="detailLoading">
        <div class="summary-identity">
          <span class="summary-order-number">订单编号 {{ value(detail.order_no) }}</span>
          <strong>{{ value(detail.apply_college_name) }} ｜ {{ major(detail) }}</strong>
          <span class="summary-meta">{{ applyYear(detail) }} ｜ {{ value(detail.apply_academy_name) }}</span>
        </div>
        <div class="summary-status">
          <div class="summary-tag-row">
            <el-tag
              :type="statusTagType(status.type)"
              :class="['status-tag', { 'status-tag-received': status.label === '被接单' }]"
              size="small"
            >
              {{ status.label }}
            </el-tag>
            <el-tag
              v-for="item in receiveTypes"
              :key="item.label"
              :type="item.type"
              :class="['receive-type-tag', item.label === '预留接单' ? 'receive-type-reserved' : 'receive-type-restarted']"
              size="mini"
              effect="plain"
            >
              {{ item.label }}
            </el-tag>
          </div>
          <span class="summary-status-tip">{{ status.tip }}</span>
        </div>
        <div class="summary-commission">
          <span>佣金</span>
          <strong>{{ commission.text }}</strong>
          <small v-if="commission.note">{{ commission.note }}</small>
        </div>
      </div>

      <div class="record-toolbar">
        <el-form class="record-filter" :inline="true" size="small">
          <el-form-item label="发送人">
            <el-input
              v-model.trim="query.handle_user_keyword"
              clearable
              placeholder="姓名/手机号/所在机构"
              @keyup.enter.native="fetchRecords(true)"
            />
          </el-form-item>
          <el-form-item label="记录类别">
            <el-select
              v-model="query.types"
              multiple
              clearable
              collapse-tags
              placeholder="全部类别"
              @change="fetchRecords(true)"
            >
              <el-option v-for="item in recordTypes" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="fetchRecords(true)">查询</el-button>
            <el-button @click="resetFilter">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="record-total">共 <strong>{{ total }}</strong> 条记录</div>
      </div>

      <el-table
        v-loading="listLoading"
        :data="records"
        row-key="id"
        border
        empty-text="暂无沟通记录"
        class="record-table"
        :header-cell-style="headerStyle"
        :cell-style="cellStyle"
      >
        <el-table-column label="记录类别" width="125" align="center">
          <template slot-scope="scope">
            <el-tag class="record-type-tag" :type="scope.row._view.typeTag" size="small">
              {{ scope.row._view.typeName }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="沟通时间" width="155" align="center">
          <template slot-scope="scope">{{ scope.row._view.createTime }}</template>
        </el-table-column>
        <el-table-column label="沟通内容" min-width="390">
          <template slot-scope="scope">
            <el-tooltip :content="scope.row._view.content" placement="top" :open-delay="300">
              <div class="record-content two-line-text">{{ scope.row._view.content }}</div>
            </el-tooltip>
            <div v-if="scope.row._view.images.length || Number(scope.row.type) === 7" class="record-content-meta">
              <span v-if="scope.row._view.images.length">
                <i class="el-icon-picture-outline" /> {{ scope.row._view.images.length }} 张图片
              </span>
              <span v-if="Number(scope.row.type) === 7"><i class="el-icon-user" /> 师资记录</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="发送人" width="165" align="center">
          <template slot-scope="scope">
            <div class="sender-cell">
              <img v-if="scope.row.msg_sender_avatar" :src="scope.row.msg_sender_avatar" @error="hideAvatar" />
              <span>{{ scope.row._view.sender }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="阅读状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag class="read-status-tag" :type="scope.row._view.readStatus.type" size="mini" effect="plain">
              {{ scope.row._view.readStatus.label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column type="expand" label="详情" width="65">
          <template slot-scope="scope">
            <div class="record-detail-panel">
              <div class="record-detail-head">
                <strong>{{ scope.row._view.typeName }}详情</strong>
                <span>记录 ID：{{ value(scope.row.id) }}</span>
              </div>
              <div class="record-detail-section">
                <label>完整内容</label>
                <p>{{ scope.row._view.content }}</p>
              </div>
              <div v-if="scope.row._view.images.length" class="record-detail-section">
                <label>记录图片</label>
                <div class="record-images">
                  <el-image
                    v-for="(url, index) in scope.row._view.images"
                    :key="`${scope.row.id}-${index}`"
                    :src="url"
                    :preview-src-list="scope.row._view.images"
                    :initial-index="index"
                    fit="cover"
                  >
                    <div slot="error" class="image-error"><i class="el-icon-picture-outline" /></div>
                  </el-image>
                </div>
              </div>
              <div v-if="Number(scope.row.type) === 7" class="record-detail-section">
                <label>提交师资</label>
                <div v-if="teacherFor(scope.row)" class="teacher-card">
                  <div><label>老师姓名</label><span>{{ value(teacherFor(scope.row).teacher_name) }}</span></div>
                  <div><label>录取年份</label><span>{{ teacherYear(teacherFor(scope.row)) }}</span></div>
                  <div><label>初试总分</label><span>{{ suffixValue(teacherFor(scope.row).teacher_first_exam_score, ' 分') }}</span></div>
                  <div><label>沟通课酬</label><span>{{ suffixValue(teacherFor(scope.row).teacher_lesson_hour_amount, ' 元/小时') }}</span></div>
                  <div><label>手机号</label><span>{{ value(teacherFor(scope.row).teacher_phone) }}</span></div>
                  <div><label>微信号</label><span>{{ value(teacherFor(scope.row).teacher_wechat) }}</span></div>
                  <div><label>QQ 号</label><span>{{ value(teacherFor(scope.row).teacher_qq) }}</span></div>
                  <div class="teacher-full"><label>备注</label><span>{{ value(teacherFor(scope.row).remark) }}</span></div>
                  <div class="teacher-full"><label>信息状态</label><span>{{ teacherVisibility(teacherFor(scope.row)) }}</span></div>
                </div>
                <div v-else-if="!isOwnTeacherRecord(scope.row)" class="teacher-empty">
                  非本人提交的师资，仅展示提交记录
                </div>
                <div v-else class="teacher-empty">
                  师资编号 {{ value(scope.row.receiver_commit_teacher_id) }}，暂无可展示的师资详情
                </div>
              </div>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="record-pagination">
        <span>第 {{ pageIndex }} / {{ Math.max(Math.ceil(total / pageSize), 1) }} 页</span>
        <el-pagination
          background
          layout="prev, pager, next, sizes, jumper"
          :current-page="pageIndex"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  matchingOrderDetail,
  matchingOrderRecordList,
  matchingOrderTeacherList,
} from '@/request/api'
import {
  MATCHING_ORDER_RECORD_TYPES,
  commissionInfo,
  formatTime,
  hasValue,
  orderMajor,
  orderReceiveTypeTags,
  orderStatusMeta,
  recordImages,
  recordSenderName,
  recordTypeName,
  recordTypeTag,
  valueOrDash,
} from './orderFormatters'

export default {
  name: 'MatchingOrderCommunicationRecordsDialog',
  props: {
    visible: { type: Boolean, default: false },
    order: { type: Object, default: () => ({}) },
    userId: { type: [String, Number], default: '' },
  },
  data() {
    return {
      detail: {},
      records: [],
      teachers: {},
      total: 0,
      pageIndex: 1,
      pageSize: 10,
      query: { handle_user_keyword: '', types: [] },
      detailLoading: false,
      listLoading: false,
      requestOrderId: null,
      recordTypes: MATCHING_ORDER_RECORD_TYPES,
      headerStyle: { textAlign: 'center', color: '#303133', background: '#f7f8fb', fontWeight: '500' },
      cellStyle: { color: '#303133', fontSize: '13px' },
    }
  },
  computed: {
    status() { return orderStatusMeta(this.detail, this.userId) },
    receiveTypes() { return orderReceiveTypeTags(this.detail) },
    commission() { return commissionInfo(this.detail) },
  },
  watch: {
    visible(value) {
      if (value) this.initialize()
    },
  },
  methods: {
    async initialize() {
      const id = this.order && this.order.id
      if (!hasValue(id)) return
      this.requestOrderId = id
      this.detail = Object.assign({}, this.order)
      this.records = []
      this.teachers = {}
      this.total = 0
      this.pageIndex = 1
      this.query = { handle_user_keyword: '', types: [] }
      await Promise.all([this.fetchDetail(id), this.fetchRecords()])
    },
    async fetchDetail(id) {
      this.detailLoading = true
      try {
        const res = await matchingOrderDetail({ id })
        if (this.requestOrderId === id && res.code === 0) {
          this.detail = Object.assign({}, this.order, res.data || {})
        }
      } catch (error) {
        // 详情失败时保留列表行携带的订单摘要。
      } finally {
        if (this.requestOrderId === id) this.detailLoading = false
      }
    },
    async fetchRecords(resetPage) {
      const id = this.order && this.order.id
      if (!hasValue(id)) return
      if (resetPage) this.pageIndex = 1
      const requestId = id
      const params = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        isPaging: 1,
        help_find_order_id: id,
      }
      if (this.query.handle_user_keyword) params.handle_user_keyword = this.query.handle_user_keyword
      if (this.query.types.length) params.types = this.query.types.join(',')
      this.listLoading = true
      try {
        const res = await matchingOrderRecordList(params)
        if (this.requestOrderId !== requestId || res.code !== 0) return
        const list = (res.data && res.data.list) || []
        this.records = list.map((record) => this.buildRecordView(record))
        this.total = Number((res.data && res.data.count) || 0)
        this.teachers = {}
        await this.fetchTeachers(this.records, requestId)
      } catch (error) {
        // 查询失败时保留当前记录，避免筛选后整张表突然清空。
      } finally {
        if (this.requestOrderId === requestId) this.listLoading = false
      }
    },
    async fetchTeachers(records, orderId) {
      const receiverIds = records
        .filter((record) => Number(record.type) === 7 && this.isOwnTeacherRecord(record))
        .map((record) => record.receiver_company_user_id)
        .filter(hasValue)
        .filter((id, index, list) => list.findIndex((item) => String(item) === String(id)) === index)
      if (!receiverIds.length) return
      const responses = await Promise.all(receiverIds.map((receiverId) => matchingOrderTeacherList({
        find_order_id: orderId,
        handle_receiver_id: receiverId,
      }).catch(() => null)))
      if (this.requestOrderId !== orderId) return
      responses.forEach((res) => {
        const list = res && res.code === 0 && res.data ? res.data.arr_receiver_teacher || [] : []
        list.forEach((teacher) => {
          if (hasValue(teacher.id)) this.$set(this.teachers, String(teacher.id), teacher)
        })
      })
    },
    resetFilter() {
      this.query = { handle_user_keyword: '', types: [] }
      this.fetchRecords(true)
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.fetchRecords(true)
    },
    handleCurrentChange(page) {
      this.pageIndex = page
      this.fetchRecords()
    },
    handleClose() {
      this.requestOrderId = null
      this.detailLoading = false
      this.listLoading = false
      this.$emit('close')
    },
    teacherFor(record) { return this.teachers[String(record.receiver_commit_teacher_id)] },
    isOwnTeacherRecord(record) {
      return hasValue(record && record.receiver_company_user_id)
        && hasValue(this.userId)
        && String(record.receiver_company_user_id) === String(this.userId)
    },
    teacherYear(teacher) { return hasValue(teacher.teacher_apply_year) ? `${teacher.teacher_apply_year}级` : '--' },
    teacherVisibility(teacher) {
      if (Number(teacher.refuse_status) === 1) return '已驳回'
      if (Number(teacher.complete_info_status) === 1 && Number(teacher.look_status) === 1) return '已解锁、已查看'
      if (Number(teacher.complete_info_status) === 1) return '已解锁、待查看'
      return '联系方式未解锁'
    },
    recordContent(record) {
      if (hasValue(record.record)) return record.record
      if (Number(record.type) === 20) return '我已查看师资，等待确认师资'
      if (Number(record.type) === 12) return '订单已完成'
      return '--'
    },
    buildRecordView(record) {
      return Object.assign({}, record, {
        _view: {
          typeName: recordTypeName(record.type),
          typeTag: recordTypeTag(record.type),
          content: this.recordContent(record),
          images: recordImages(record),
          sender: recordSenderName(record),
          readStatus: this.readStatusMeta(record),
          createTime: formatTime(this.$moment, record.create_time),
        },
      })
    },
    readStatusMeta(record) {
      if (!hasValue(record.msg_read_status)) return { label: '--', type: 'info' }
      return Number(record.msg_read_status) === 0
        ? { label: '未读', type: 'danger' }
        : { label: '已读', type: 'success' }
    },
    statusTagType(type) { return type === 'primary' ? '' : type },
    major(row) { return orderMajor(row) },
    value(value) { return valueOrDash(value) },
    applyYear(row) { return hasValue(row.apply_year) ? `${row.apply_year}考研` : '--' },
    suffixValue(value, suffix) { return hasValue(value) ? `${value}${suffix}` : '--' },
    hideAvatar(event) { event.target.style.display = 'none' },
  },
}
</script>

<style lang="less" scoped>
.record-dialog { min-height: 360px; }
.order-summary { align-items: center; border-bottom: 1px solid #e8eaf0; display: grid; gap: 22px; grid-template-columns: minmax(320px, 1.5fr) minmax(260px, 1fr) 170px; margin-bottom: 14px; min-height: 62px; padding: 0 0 14px; }
.summary-identity { display: flex; flex-direction: column; min-width: 0; }
.summary-order-number { color: #303133; font-family: Consolas, 'Courier New', monospace; font-size: 12px; font-weight: 600; margin-bottom: 5px; }
.summary-identity strong { color: #303133; font-size: 15px; line-height: 22px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.summary-meta { color: #303133; font-size: 12px; line-height: 18px; margin-top: 4px; }
.summary-status-tip { color: #8a8f98; font-size: 12px; line-height: 18px; margin-top: 4px; }
.summary-status { align-items: flex-start; display: flex; flex-direction: column; min-width: 0; }
.summary-tag-row { align-items: center; display: flex; flex-wrap: wrap; gap: 6px; }
.status-tag { background: #f1f3f9 !important; border-color: #d9deed !important; color: #59679b !important; }
.status-tag.el-tag--danger { background: #fff1f0 !important; border-color: #f5c8c5 !important; color: #d95750 !important; }
.status-tag.el-tag--warning { background: #fff7e8 !important; border-color: #efd6a9 !important; color: #bb7721 !important; }
.status-tag.el-tag--success { background: #eff8f2 !important; border-color: #c7e4d0 !important; color: #438c59 !important; }
.status-tag.el-tag--info { background: #f4f4f5 !important; border-color: #dedfe1 !important; color: #303133 !important; }
.status-tag-received { background: #f1f3f9 !important; border-color: #d9deed !important; color: #59679b !important; }
.receive-type-reserved { background: #eff8f5 !important; border-color: #bfdfd5 !important; color: #328a74 !important; }
.receive-type-restarted { background: #fff7e8 !important; border-color: #efd6a9 !important; color: #b97925 !important; }
.summary-status-tip { -webkit-box-orient: vertical; -webkit-line-clamp: 2; display: -webkit-box; overflow: hidden; }
.summary-commission { align-items: flex-end; display: flex; flex-direction: column; min-width: 150px; }
.summary-commission > span { color: #303133; font-size: 12px; }
.summary-commission strong { color: #ef5b53; font-size: 21px; line-height: 29px; }
.summary-commission small { color: #d98b28; font-size: 11px; text-align: right; }
.record-toolbar { align-items: flex-start; border-bottom: 1px dashed #e8eaf0; display: flex; justify-content: space-between; margin-bottom: 12px; }
.record-filter { flex: 1; }
.record-filter /deep/ .el-form-item { margin-bottom: 12px; }
.record-filter .el-input { width: 210px; }
.record-filter .el-select { width: 220px; }
.record-total { color: #303133; flex: 0 0 auto; font-size: 13px; line-height: 32px; margin-left: 18px; }
.record-total strong { color: #59679b; font-size: 15px; }
.record-content { color: #303133; line-height: 20px; overflow-wrap: break-word; }
.two-line-text { -webkit-box-orient: vertical; -webkit-line-clamp: 2; display: -webkit-box; overflow: hidden; }
.record-content-meta { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 5px; }
.record-content-meta span { color: #303133; font-size: 12px; }
.record-dialog /deep/ .el-table__cell { padding: 10px 0; }
.record-table /deep/ th.el-table__cell { background: #f7f8fb !important; color: #303133 !important; }
.record-table /deep/ td.el-table__cell, .record-table /deep/ th.el-table__cell { border-color: #e8eaf0; }
.record-table /deep/ .el-table__body tr:hover > td.el-table__cell { background: #f6f7fb; }
.sender-cell { align-items: center; display: flex; justify-content: center; min-height: 34px; }
.sender-cell img { border-radius: 50%; height: 30px; margin-right: 7px; object-fit: cover; width: 30px; }
.sender-cell span { color: #303133; max-width: 110px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.record-detail-panel { background: #f7f8fb; border-left: 3px solid #59679b; margin: 2px 18px; padding: 14px 18px; }
.record-detail-head { align-items: center; border-bottom: 1px solid #e8eaf0; display: flex; justify-content: space-between; padding-bottom: 10px; }
.record-detail-head strong { color: #303133; font-size: 14px; }
.record-detail-head span { color: #303133; font-family: Consolas, 'Courier New', monospace; font-size: 12px; }
.record-detail-section { margin-top: 12px; }
.record-detail-section > label { color: #303133; display: block; font-size: 12px; margin-bottom: 6px; }
.record-detail-section > p { color: #303133; line-height: 21px; margin: 0; overflow-wrap: break-word; white-space: pre-wrap; }
.record-images { display: flex; flex-wrap: wrap; gap: 8px; }
.record-images .el-image { border: 1px solid #e8eaf0; border-radius: 5px; height: 82px; width: 82px; }
.image-error { align-items: center; color: #59679b; display: flex; font-size: 22px; height: 100%; justify-content: center; }
.teacher-card { background: #fff; border: 1px solid #e8eaf0; border-radius: 6px; display: flex; flex-wrap: wrap; gap: 8px 24px; padding: 12px 14px; }
.teacher-card > div { display: flex; min-width: 210px; width: calc(50% - 12px); }
.teacher-card label { color: #303133; flex: 0 0 72px; }
.teacher-card span { color: #303133; min-width: 0; overflow-wrap: break-word; }
.teacher-card .teacher-full { width: 100%; }
.teacher-empty { background: #f7f8fb; border-radius: 4px; color: #303133; font-size: 12px; padding: 10px 12px; }
.record-pagination { align-items: center; color: #303133; display: flex; font-size: 13px; justify-content: space-between; margin-top: 14px; }
@media screen and (max-width: 1100px) {
  .order-summary { align-items: flex-start; grid-template-columns: 1fr 1fr; }
  .summary-commission { align-items: flex-start; }
  .record-toolbar { flex-direction: column; }
  .record-total { margin: -5px 0 8px; }
}
</style>

<style lang="less">
.matching-record-dialog { display: flex; flex-direction: column; margin-bottom: 3vh; max-height: 94vh; max-width: 1480px; min-width: 960px; }
.matching-record-dialog .el-dialog__header { flex: 0 0 auto; }
.matching-record-dialog .el-dialog__body { flex: 1 1 auto; min-height: 0; overflow-y: auto; padding: 14px 20px 18px; }
.matching-record-dialog .el-button--primary { background: #59679b; border-color: #59679b; }
.matching-record-dialog .el-button--primary:focus,
.matching-record-dialog .el-button--primary:hover { background: #6977a8; border-color: #6977a8; }
.matching-record-dialog .el-link.el-link--primary { color: #59679b; }
.matching-record-dialog .el-link.el-link--primary:hover { color: #6977a8; }
.matching-record-dialog .el-input__inner:focus,
.matching-record-dialog .el-select .el-input.is-focus .el-input__inner { border-color: #59679b; }
.matching-record-dialog .el-pagination.is-background .el-pager li:not(.disabled).active { background: #59679b; }
.matching-record-dialog .record-type-tag { background: #f1f3f9; border-color: #d9deed; color: #59679b; }
.matching-record-dialog .record-type-tag.el-tag--success,
.matching-record-dialog .read-status-tag.el-tag--success { background: #eff8f2; border-color: #c7e4d0; color: #438c59; }
.matching-record-dialog .record-type-tag.el-tag--danger,
.matching-record-dialog .read-status-tag.el-tag--danger { background: #fff1f0; border-color: #f5c8c5; color: #d95750; }
.matching-record-dialog .record-type-tag.el-tag--warning,
.matching-record-dialog .read-status-tag.el-tag--warning { background: #fff7e8; border-color: #efd6a9; color: #bb7721; }
.matching-record-dialog .record-type-tag.el-tag--info,
.matching-record-dialog .read-status-tag.el-tag--info { background: #f4f4f5; border-color: #dedfe1; color: #303133; }
@media screen and (max-width: 1050px) {
  .matching-record-dialog { min-width: 0; width: calc(100% - 24px) !important; }
}
</style>
